import { z } from 'zod'

import { apiClient } from '@/lib/api/client'
import { wireDateTimeOptional } from '@/lib/api/schemas'
import type {
  BpmnDiagram,
  BpmnDiagramJson,
  BpmnDiagramVersion,
  BpmnGeneratedBy,
} from '@/types/bpmn'

// -----------------------------------------------------------------------------
// Zod — diagram_json
// -----------------------------------------------------------------------------

const bpmnNodeSchema = z
  .object({
    id: z.string().min(1),
    type: z.string().min(1),
    position: z.object({ x: z.number(), y: z.number() }),
    data: z
      .object({
        label: z.string(),
      })
      .passthrough(),
  })
  .passthrough()

const bpmnEdgeSchema = z
  .object({
    id: z.string().min(1),
    source: z.string().min(1),
    target: z.string().min(1),
    type: z.literal('sequence_flow').optional(),
    label: z.string().optional(),
  })
  .passthrough()

export const bpmnDiagramJsonSchema = z.object({
  nodes: z.array(bpmnNodeSchema).default([]),
  edges: z.array(bpmnEdgeSchema).default([]),
}) as unknown as z.ZodType<BpmnDiagramJson>

const bpmnGeneratedBySchema = z.enum(['manual', 'athena', 'daedalus', 'imported'])

// Backend Supabase retorna snake_case; MSW usa camelCase — normaliza antes do parse.
function normalizeBpmnDiagramKeys(raw: unknown): unknown {
  if (typeof raw !== 'object' || raw === null) return raw
  const obj = raw as Record<string, unknown>
  return {
    id: obj.id,
    companyId: obj.companyId ?? obj.company_id,
    linkedSipocProcessId:
      'linkedSipocProcessId' in obj ? obj.linkedSipocProcessId : obj.linked_sipoc_process_id ?? null,
    linkedWorkflowId:
      'linkedWorkflowId' in obj ? obj.linkedWorkflowId : obj.linked_workflow_id ?? null,
    linkedGoalId: 'linkedGoalId' in obj ? obj.linkedGoalId : obj.linked_goal_id ?? null,
    name: obj.name,
    description: obj.description ?? null,
    diagramJson: obj.diagramJson ?? obj.diagram_json ?? { nodes: [], edges: [] },
    version: obj.version ?? 1,
    generatedBy: obj.generatedBy ?? obj.generated_by ?? 'manual',
    generatedByTaskId:
      'generatedByTaskId' in obj ? obj.generatedByTaskId : obj.generated_by_task_id ?? null,
    createdAt: obj.createdAt ?? obj.created_at ?? null,
    updatedAt: obj.updatedAt ?? obj.updated_at ?? null,
  }
}

function normalizeBpmnVersionKeys(raw: unknown): unknown {
  if (typeof raw !== 'object' || raw === null) return raw
  const obj = raw as Record<string, unknown>
  return {
    id: obj.id,
    diagramId: obj.diagramId ?? obj.diagram_id,
    version: obj.version,
    diagramJson: obj.diagramJson ?? obj.diagram_json ?? { nodes: [], edges: [] },
    changedByUserId:
      'changedByUserId' in obj ? obj.changedByUserId : obj.changed_by_user_id ?? null,
    changeNotes: obj.changeNotes ?? obj.change_notes ?? null,
    createdAt: obj.createdAt ?? obj.created_at ?? null,
  }
}

export const bpmnDiagramSchema = z.preprocess(
  normalizeBpmnDiagramKeys,
  z.object({
    id: z.string().min(1),
    companyId: z.string().min(1),
    linkedSipocProcessId: z.string().min(1).nullable(),
    linkedWorkflowId: z.string().min(1).nullable(),
    linkedGoalId: z.string().min(1).nullable(),
    name: z.string().min(1),
    description: z.string().nullable(),
    diagramJson: bpmnDiagramJsonSchema,
    version: z.number().int().nonnegative(),
    generatedBy: bpmnGeneratedBySchema,
    generatedByTaskId: z.string().min(1).nullable(),
    createdAt: wireDateTimeOptional,
    updatedAt: wireDateTimeOptional,
  }),
) as unknown as z.ZodType<BpmnDiagram>

export const bpmnDiagramListSchema = z.array(bpmnDiagramSchema)

export const bpmnDiagramVersionSchema = z.preprocess(
  normalizeBpmnVersionKeys,
  z.object({
    id: z.string().min(1),
    diagramId: z.string().min(1),
    version: z.number().int().nonnegative(),
    diagramJson: bpmnDiagramJsonSchema,
    changedByUserId: z.string().min(1).nullable(),
    changeNotes: z.string().nullable(),
    createdAt: wireDateTimeOptional,
  }),
) as unknown as z.ZodType<BpmnDiagramVersion>

export const bpmnDiagramVersionListSchema = z.array(bpmnDiagramVersionSchema)

// -----------------------------------------------------------------------------
// Filters & bodies
// -----------------------------------------------------------------------------

export type ListBpmnDiagramsFilters = {
  linked_sipoc_process_id?: string
  linked_workflow_id?: string
  linked_goal_id?: string
  generated_by?: BpmnGeneratedBy
}

export type CreateBpmnDiagramBody = {
  name: string
  diagramJson: BpmnDiagramJson
  description?: string | null
  generatedBy?: BpmnGeneratedBy
  generatedByTaskId?: string | null
  linkedSipocProcessId?: string | null
  linkedWorkflowId?: string | null
  linkedGoalId?: string | null
}

export type UpdateBpmnDiagramBody = Partial<{
  name: string
  description: string | null
  diagramJson: BpmnDiagramJson
  linkedSipocProcessId: string | null
  linkedWorkflowId: string | null
  linkedGoalId: string | null
}>

function toCreateWire(body: CreateBpmnDiagramBody): Record<string, unknown> {
  return {
    name: body.name,
    diagram_json: body.diagramJson,
    description: body.description,
    generated_by: body.generatedBy ?? 'manual',
    generated_by_task_id: body.generatedByTaskId,
    linked_sipoc_process_id: body.linkedSipocProcessId,
    linked_workflow_id: body.linkedWorkflowId,
    linked_goal_id: body.linkedGoalId,
  }
}

function toUpdateWire(body: UpdateBpmnDiagramBody): Record<string, unknown> {
  const wire: Record<string, unknown> = {}
  if (body.name !== undefined) wire.name = body.name
  if (body.description !== undefined) wire.description = body.description
  if (body.diagramJson !== undefined) wire.diagram_json = body.diagramJson
  if (body.linkedSipocProcessId !== undefined)
    wire.linked_sipoc_process_id = body.linkedSipocProcessId
  if (body.linkedWorkflowId !== undefined) wire.linked_workflow_id = body.linkedWorkflowId
  if (body.linkedGoalId !== undefined) wire.linked_goal_id = body.linkedGoalId
  return wire
}

// -----------------------------------------------------------------------------
// API functions
// -----------------------------------------------------------------------------

export function listCompanyBpmnDiagrams(
  companyId: string,
  filters: ListBpmnDiagramsFilters = {},
): Promise<BpmnDiagram[]> {
  const qs = new URLSearchParams()
  if (filters.linked_sipoc_process_id)
    qs.set('linked_sipoc_process_id', filters.linked_sipoc_process_id)
  if (filters.linked_workflow_id) qs.set('linked_workflow_id', filters.linked_workflow_id)
  if (filters.linked_goal_id) qs.set('linked_goal_id', filters.linked_goal_id)
  if (filters.generated_by) qs.set('generated_by', filters.generated_by)
  const tail = qs.toString() ? `?${qs}` : ''
  return apiClient.requestValidated(
    `/companies/${encodeURIComponent(companyId)}/bpmn/diagrams${tail}`,
    bpmnDiagramListSchema,
  )
}

export function getBpmnDiagram(id: string): Promise<BpmnDiagram> {
  return apiClient.requestValidated(
    `/bpmn/diagrams/${encodeURIComponent(id)}`,
    bpmnDiagramSchema,
  )
}

export function createBpmnDiagram(body: CreateBpmnDiagramBody): Promise<BpmnDiagram> {
  return apiClient.requestValidated('/bpmn/diagrams', bpmnDiagramSchema, {
    method: 'POST',
    body: JSON.stringify(toCreateWire(body)),
  })
}

export function updateBpmnDiagram(
  id: string,
  body: UpdateBpmnDiagramBody,
): Promise<BpmnDiagram> {
  return apiClient.requestValidated(
    `/bpmn/diagrams/${encodeURIComponent(id)}`,
    bpmnDiagramSchema,
    { method: 'PATCH', body: JSON.stringify(toUpdateWire(body)) },
  )
}

export function deleteBpmnDiagram(id: string): Promise<void> {
  return apiClient.request(`/bpmn/diagrams/${encodeURIComponent(id)}`, {
    method: 'DELETE',
  })
}

export function duplicateBpmnDiagram(
  id: string,
  body?: { name?: string },
): Promise<BpmnDiagram> {
  return apiClient.requestValidated(
    `/bpmn/diagrams/${encodeURIComponent(id)}/duplicate`,
    bpmnDiagramSchema,
    {
      method: 'POST',
      ...(body !== undefined && { body: JSON.stringify(body) }),
    },
  )
}

export function listBpmnDiagramVersions(id: string): Promise<BpmnDiagramVersion[]> {
  return apiClient.requestValidated(
    `/bpmn/diagrams/${encodeURIComponent(id)}/versions`,
    bpmnDiagramVersionListSchema,
  )
}
